/**
 * This class for Creating SessionDataProvider instance,
 * to utilize it for getting the page context
 */
import type { VPC, SessionData } from "./types.js";
import { SessionDataParser } from "./sessionDataParser.js";
import { isUserOptedIntoPrism } from "../prism/index.js";

export class SessionDataProvider {
  private static sessionDataProviderInstance: SessionDataProvider;
  private readonly dataParser: SessionDataParser;

  protected constructor(instance: SessionDataParser) {
    this.dataParser = instance;
  }

  /**
   * Validate an object to see if it contains the required keys for session data
   *
   * @param {any} maybeSessionData - an object that needs to be validated
   * @returns {boolean} if the session data object passed in was valid
   */
  public static isValidSessionData = (maybeSessionData: any): boolean => {
    try {
      SessionDataParser.validateSessionData(maybeSessionData);
      return true;
    } catch (err: unknown) {
      return false;
    }
  };

  /**
   * Validate an object to see if it contains the required keys for session data, throw error if invalid
   *
   * @param {any} maybeSessionData - an object that needs to be validated
   * @returns {boolean} true if the session data object passed in was valid, throws error if invalid
   */
  public static validateSessionData = (maybeSessionData: any): boolean => {
    SessionDataParser.validateSessionData(maybeSessionData);
    return true;
  };

  /*
   * Return SessionDataProvider instance for singleton class.
   */
  public static getInstance(): SessionDataProvider {
    if (!this.sessionDataProviderInstance) {
      this.sessionDataProviderInstance = new SessionDataProvider(SessionDataParser.getInstance());
    }
    return this.sessionDataProviderInstance;
  }

  /**
   * @returns sessionARN: Promise<string>
   * returns empty string when falsy
   */
  public async getSessionARN(): Promise<string> {
    const sessionData = this.dataParser.getSessionData();
    return sessionData["sessionARN"] || "";
  }

  /**
   * @returns accountId: Promise<string>
   * returns empty string when falsy
   */
  public async getAccountId(): Promise<string> {
    const sessionData = this.dataParser.getSessionData();
    return sessionData["accountId"] || "";
  }

  /**
   * @returns identityToken: Promise<string>
   * returns empty string when falsy
   */
  public async getIdentityToken(): Promise<string> {
    const sessionData = this.dataParser.getSessionData();
    return sessionData["identityToken"] || "";
  }

  /**
   * @returns sessionId: Promise<string | undefined>
   */
  public async getSessionId(): Promise<string | undefined> {
    const sessionData = this.dataParser.getSessionData();
    return sessionData["sessionId"];
  }

  /**
   * This method provides the region the page is served from.
   * returns empty string when falsy
   * @returns region: Promise<string>
   */
  public async getInfrastructureRegion(): Promise<string> {
    const sessionData = this.dataParser.getSessionData();
    return sessionData["infrastructureRegion"] || "";
  }

  /**
   * This method provides the VPC if it exists.
   *
   * @returns vpc: Promise<VPC | undefined>
   */
  public async getVpc(): Promise<VPC | undefined> {
    const sessionData = this.dataParser.getSessionData();
    let result;
    if (sessionData) {
      result = sessionData["vpc"];
    }
    return result;
  }

  /**
   * This method provides the accountAlias if it exists.
   *
   * @returns accountAlias: Promise<string | undefined>
   */
  public async getAccountAlias(): Promise<string | undefined> {
    const sessionData = this.dataParser.getSessionData();
    return sessionData["accountAlias"];
  }

  /**
   * This method provides the displayName if it exists.
   *
   * @returns displayName: Promise<string | undefined>
   */
  public async getDisplayName(): Promise<string | undefined> {
    const sessionData = this.dataParser.getSessionData();
    return sessionData["displayName"];
  }

  /**
   * This method provides the sessionDifferentiator if it exists.
   *
   * @returns sessionDifferentiator: Promise<string | undefined>
   */
  public async getSessionDifferentiator(): Promise<string | undefined> {
    const sessionData = this.dataParser.getSessionData();
    return sessionData["sessionDifferentiator"];
  }

  /**
   * This method provides an object with current session information and some of the fields are optional
   * and may not be always present. If you only care about getting individual properties, use the respective function such as
   * getAccountId
   * @returns SessionData
   */
  public async getSessionData(): Promise<SessionData> {
    const sessionData = this.dataParser.getSessionData();

    if (!sessionData["prismModeEnabled"]) {
      sessionData.prismModeEnabled = isUserOptedIntoPrism();
    }

    return sessionData;
  }

  /**
   * This method provides the state of prismModeEnabled if it exists
   *
   * @returns isMultiSessionEnabled: Promise<boolean | undefined>
   */
  public async isMultiSessionEnabled(): Promise<boolean | undefined> {
    const sessionData = this.dataParser.getSessionData();
    if (!sessionData["prismModeEnabled"]) {
      return isUserOptedIntoPrism();
    }
    return sessionData["prismModeEnabled"];
  }
}
